import { ErrorResponseStatus, ERROR_RESPONSE_STATUS } from 'fetcher';
import { FormikErrors } from 'formik';
import { FormPageInputData } from 'shared';
import { ValidationErrors } from 'validators';
import {
  useSetCurrentPageStateDispatch,
  useUpdatePageDispatch,
  updatePage,
  CURRENT_PAGE_STATE_TYPE,
  setCurrentPageState,
  useCurrentPageState,
} from '../../store';
import {
  SubmissionResult,
  SUBMISSION_RESULT_TYPE,
  SubmissionFailure,
  SubmissionSuccess,
  convertPageTypeIntoCurrentPageStateType,
  SubmissionFileUploadValidationError,
  SubmissionUnauthorized,
} from '../shared';
import { FormErrors } from '../useFormErrors';

export const HANDLED_SUBMISSION_RESULT_STATE_STATUS = {
  unrecoverableError: 'unrecoverableError',
  submissionError: 'submissionError',
  validationError: 'validationError',
  unauthorizedError: 'unauthorizedError',
  fileUploadValidationError: 'fileUploadValidationError',
  globalError: 'globalError',
  success: 'success',
} as const;

type HandledStateUnrecoverableError = {
  status: typeof HANDLED_SUBMISSION_RESULT_STATE_STATUS.unrecoverableError;
};

type HandledStateSubmissionError = {
  status: typeof HANDLED_SUBMISSION_RESULT_STATE_STATUS.submissionError;
  error: {
    submission: FormErrors['submission'];
  };
};

type HandledStateValidationError = {
  status: typeof HANDLED_SUBMISSION_RESULT_STATE_STATUS.validationError;
  error: {
    global?: FormErrors['global'];
    properties?: FormikErrors<FormPageInputData>;
  };
};

type HandledStateFileUploadValidationError = {
  status: typeof HANDLED_SUBMISSION_RESULT_STATE_STATUS.fileUploadValidationError;
  errorMessage: string;
  unrecoverable: boolean;
};

type HandledStateUnauthorizedError = {
  status: typeof HANDLED_SUBMISSION_RESULT_STATE_STATUS.unauthorizedError;
};

type HandledStateSuccess = {
  status: typeof HANDLED_SUBMISSION_RESULT_STATE_STATUS.success;
};

type HandledStateGlobalError = {
  status: typeof HANDLED_SUBMISSION_RESULT_STATE_STATUS.globalError;
  error: {
    global?: FormErrors['global'];
  };
};

const useHandler = () => {
  const currentPageState = useCurrentPageState();
  const dispatchSetCurrentPageState = useSetCurrentPageStateDispatch();
  const dispatchUpdatePage = useUpdatePageDispatch();

  const handleFailure = (
    result: SubmissionFailure,
  ): HandledStateUnrecoverableError | HandledStateSubmissionError => {
    const { status, error } = result;
    if (status === ERROR_RESPONSE_STATUS.maintenance) {
      dispatchSetCurrentPageState(
        setCurrentPageState({
          type: CURRENT_PAGE_STATE_TYPE.maintenance,
          error: error,
        }),
      );
      return {
        status: HANDLED_SUBMISSION_RESULT_STATE_STATUS.unrecoverableError,
      };
    }
    if (!needTransitionErrorPage(status)) {
      return {
        status: HANDLED_SUBMISSION_RESULT_STATE_STATUS.submissionError,
        error: {
          submission: [error.japanese],
        },
      };
    }
    dispatchSetCurrentPageState(
      setCurrentPageState({
        type: CURRENT_PAGE_STATE_TYPE.error,
        error,
      }),
    );
    return {
      status: HANDLED_SUBMISSION_RESULT_STATE_STATUS.unrecoverableError,
    };
  };

  const handleUnauthorized = (
    result: SubmissionUnauthorized,
  ): HandledStateUnauthorizedError => {
    const {
      formPage,
      reCaptchaSetting,
      error,
      trackingSetting,
      externalMeasurementTag,
      apiEndpoint,
      fileUploadSetting,
    } = result;
    const needsScrollTopAndFocus =
      currentPageState.type !== CURRENT_PAGE_STATE_TYPE.authentication;
    dispatchUpdatePage(
      updatePage({
        nextFormPage: formPage,
        reCaptchaSetting,
        trackingSetting,
        needsScrollTopAndFocus,
        nextPageState: {
          type: convertPageTypeIntoCurrentPageStateType(formPage.pageType),
        },
        inputData: {},
        globalErrors: error?.global,
        externalMeasurementTag,
        apiEndpoint,
        fileUploadSetting,
      }),
    );
    return {
      status: HANDLED_SUBMISSION_RESULT_STATE_STATUS.unauthorizedError,
    };
  };

  const handleFileUploadValidationError = (
    result: SubmissionFileUploadValidationError,
  ): HandledStateFileUploadValidationError => {
    const { unrecoverable, errorMessage } = result;
    return {
      status: HANDLED_SUBMISSION_RESULT_STATE_STATUS.fileUploadValidationError,
      unrecoverable,
      errorMessage,
    };
  };

  const handleSuccess = (
    result: SubmissionSuccess,
  ):
    | HandledStateValidationError
    | HandledStateSuccess
    | HandledStateGlobalError => {
    const {
      formPage,
      inputData,
      reCaptchaSetting,
      error,
      trackingSetting,
      authentication,
      externalMeasurementTag,
      apiEndpoint,
      fileUploadSetting,
    } = result;
    dispatchUpdatePage(
      updatePage({
        nextFormPage: formPage,
        inputData,
        nextPageState: {
          type: convertPageTypeIntoCurrentPageStateType(formPage.pageType),
        },
        reCaptchaSetting,
        trackingSetting,
        authentication,
        needsScrollTopAndFocus: error == null,
        externalMeasurementTag,
        apiEndpoint,
        fileUploadSetting,
        globalErrors: error?.global,
      }),
    );
    if (!error) {
      return {
        status: HANDLED_SUBMISSION_RESULT_STATE_STATUS.success,
      };
    }
    const { global, properties } = error;
    if (properties == null) {
      return {
        status: HANDLED_SUBMISSION_RESULT_STATE_STATUS.globalError,
        error: {
          global,
        },
      };
    }
    const propertyErrors = properties?.reduce(
      (acc, { propertyId, errorMessages }) => ({
        ...acc,
        [propertyId]: errorMessages,
      }),
      {} as ValidationErrors,
    );
    return {
      status: HANDLED_SUBMISSION_RESULT_STATE_STATUS.validationError,
      error: {
        global,
        properties: propertyErrors as FormikErrors<FormPageInputData>,
      },
    };
  };

  return {
    handleFailure,
    handleSuccess,
    handleUnauthorized,
    handleFileUploadValidationError,
  };
};

const needTransitionErrorPage = (status: ErrorResponseStatus): boolean =>
  status === ERROR_RESPONSE_STATUS.badRequest ||
  status === ERROR_RESPONSE_STATUS.notFound;

export type HandleSubmissionResult = (
  result: SubmissionResult,
) =>
  | HandledStateUnrecoverableError
  | HandledStateSubmissionError
  | HandledStateValidationError
  | HandledStateUnauthorizedError
  | HandledStateFileUploadValidationError
  | HandledStateGlobalError
  | HandledStateSuccess;

export const useHandleSubmissionResult = (): {
  handleSubmissionResult: HandleSubmissionResult;
} => {
  const {
    handleFailure,
    handleSuccess,
    handleUnauthorized,
    handleFileUploadValidationError,
  } = useHandler();
  const handleSubmissionResult: HandleSubmissionResult = (
    result: SubmissionResult,
  ) => {
    if (result.type === SUBMISSION_RESULT_TYPE.failure) {
      return handleFailure(result);
    }
    if (result.type === SUBMISSION_RESULT_TYPE.unauthorized) {
      return handleUnauthorized(result);
    }
    if (result.type === SUBMISSION_RESULT_TYPE.fileUploadValidationError) {
      return handleFileUploadValidationError(result);
    }
    return handleSuccess(result);
  };
  return {
    handleSubmissionResult,
  };
};
